import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../../../src/image/THOUGHTCODES-logo.png";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "About", href: "/about" },
  { label: "Past Performance", href: "/past-performance" },
  // { label: "Capability", href: "/capabilities" },
];


const Navbar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const routePath = location.pathname.split("/")[1];
  const closeMenu = () => setMenuOpen(false);

  const links = useMemo(() => navLinks, []);

  return (
    <>
      <div className="border-b !border-white/5 bg-[#0b1325]">
        <div className="mx-auto flex container items-center justify-between py-2.5 text-sm leading-5 text-slate-200">
          <div className="flex min-w-0 items-center gap-3">
            <a
              href="mailto:info@thoughtcodes.com"
              className="inline-flex min-w-0 items-center gap-1.5 hover:text-white"
            >
              <svg
                className="h-4 w-4 shrink-0 text-[#dbe6ff]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75M21.75 6.75A2.25 2.25 0 0019.5 4.5h-15A2.25 2.25 0 002.25 6.75m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
              <span className="truncate text-[#dbe6ff]">
                info@thoughtcodes.com
              </span>
            </a>
            <span className="text-slate-500">|</span>
            <a
              href="tel:+7032312569"
              className="inline-flex items-center gap-1.5 whitespace-nowrap hover:text-white"
            >
              <svg
                className="h-4 w-4 shrink-0 text-[#dbe6ff]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106a1.125 1.125 0 00-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.272.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.09-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
                />
              </svg>
              <span className="text-[#dbe6ff]">+ (703) 231-2569</span>
            </a>
          </div>
          <p className="hidden items-center gap-2 sm:flex text-[#dbe6ff] text-sm font-normal">
            <span>SBA Certified SDB</span>
            <span className="text-[#dbe6ff]">|</span>
            <span>SWaM Certified</span>
            <span className="text-[#dbe6ff]">|</span>
            <span className="text-[#dbe6ff]">ISO Certified</span>
          </p>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-white bg-white backdrop-blur-xl shadow-sm">
        <nav className="mx-auto flex container items-center justify-between py-3">
          <div className="flex items-center gap-2">
            <Link to="/">
              <img src={logo} alt="Logo" className="h-9 w-auto sm:h-auto" />
            </Link>
          </div>
          <div className="hidden items-center gap-4 md:flex">
            <ul className="flex items-center !gap-4 text-sm">
              {links.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className={`text-base font-medium transition ${routePath === link.href.split("/")[1] ? "text-[var(--brand)]" : "text-black hover:text-[var(--brand)]"}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <a
              href="/contact"
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl button-gradient px-8 py-3 text-base font-medium text-white"
            >
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/20 via-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              Get in Touch
            </a>
          </div>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border !border-slate-300 !bg-white text-black md:hidden"
            aria-label="Open navigation"
          >
            <span className="relative block h-0.5 w-4 rounded-full bg-black">
              <span className="absolute left-0 top-[-6px] h-0.5 w-4 rounded-full bg-black" />
              <span className="absolute left-0 top-[6px] h-0.5 w-4 rounded-full bg-black" />
            </span>
          </button>
        </nav>
        {/* Mobile dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="md:hidden"
            >
              <div className="mx-auto max-w-7xl px-4 pb-4">
                <div className="mt-2 rounded-2xl border !border-slate-200 bg-white p-4 shadow-soft-glass/40 backdrop-blur-xl">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-600"></p>
                    <button
                      onClick={closeMenu}
                      aria-label="Close navigation"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border !border-slate-300 !bg-white text-black transition hover:bg-slate-100"
                    >
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden
                      >
                        <path
                          d="M5 5l10 10M15 5L5 15"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>

                  <ul className="space-y-1">
                    {links.map((link) => (
                      <li key={link.href}>
                        <NavLink
                          to={link.href}
                          end={link.href === "/"}
                          onClick={closeMenu}
                          className={({ isActive }) =>
                            [
                              "block rounded-xl px-3 py-2 text-sm transition",
                              isActive
                                ? "text-[var(--brand)] bg-slate-50"
                                : "text-black hover:bg-slate-100 hover:text-[var(--brand)]",
                            ].join(" ")
                          }
                        >
                          {link.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/contact"
                    onClick={closeMenu}
                    className="group relative w-full mt-5 inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl button-gradient px-6 py-2 text-base font-medium text-white"
                  >
                    <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/20 via-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    Get in Touch
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navbar;

